import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import {
  obterRelatorioLider,
  RelatorioLiderResponse,
  formatarPorcentagem,
  formatarTempoConclusao,
  obterCorTaxaConclusao,
  FiltrosRelatorio
} from '../../services/relatorioService';
import { exportarRelatorioLider } from '../../services/exportacaoService';
import { useAuth } from '../../contexts/AuthContext';
import TempoMedioPorStatus from './TempoMedioPorStatus';
import ProdutividadeDetalhada from './ProdutividadeDetalhada';
import SkeletonLoader from '../common/SkeletonLoader';
import TarefasMaisAntigas from './TarefasMaisAntigas';
import GargalosIdentificados from './GargalosIdentificados';
import FiltrosRelatorioComponent from './FiltrosRelatorio';

const RelatorioLider: React.FC = () => {
  const { usuario } = useAuth();
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string>('');
  const [relatorio, setRelatorio] = useState<RelatorioLiderResponse | null>(null);
  const [relatorioGeral, setRelatorioGeral] = useState<RelatorioLiderResponse | null>(null);
  const [filtrosAtivos, setFiltrosAtivos] = useState<FiltrosRelatorio>({});

  const carregarRelatorio = async (periodo?: { dataInicio: string; dataFim: string }, filtros?: FiltrosRelatorio) => {
    setCarregando(true);
    setErro('');

    try {
      const filtrosParaUsar = filtros || filtrosAtivos;
      
      // Usar datas dos filtros se disponíveis, senão usar período
      let dataInicio: string | undefined;
      let dataFim: string | undefined;

      if (filtrosParaUsar.dataInicio || filtrosParaUsar.dataFim) {
        dataInicio = filtrosParaUsar.dataInicio;
        dataFim = filtrosParaUsar.dataFim;
      } else if (periodo) {
        dataInicio = periodo.dataInicio;
        dataFim = periodo.dataFim;
      }

      const dados = await obterRelatorioLider(dataInicio, dataFim, filtrosParaUsar);
      setRelatorio(dados);
      
      // Se não há filtros ativos, também atualizar relatório geral
      if (!filtrosParaUsar || Object.keys(filtrosParaUsar).length === 0 || 
          (!filtrosParaUsar.status && !filtrosParaUsar.prioridades && !filtrosParaUsar.usuarios)) {
        setRelatorioGeral(dados);
      } else {
        // Carregar dados gerais em paralelo para exportação geral
        const dadosGerais = await obterRelatorioLider(dataInicio, dataFim, {});
        setRelatorioGeral(dadosGerais);
      }
    } catch (error: any) {
      setErro(error.message || 'Erro ao carregar relatório');
      console.error('Erro ao carregar relatório:', error);
    } finally {
      setCarregando(false);
    }
  };

  const handleFiltrosChange = (novosFiltros: FiltrosRelatorio) => {
    setFiltrosAtivos(novosFiltros);
    carregarRelatorio(undefined, novosFiltros);
  };

  const handleExportarFiltrado = () => {
    if (relatorio) {
      console.log('[RelatorioLider] Exportando com filtros:', filtrosAtivos);
      exportarRelatorioLider(relatorio, filtrosAtivos);
    }
  };

  const handleExportarGeral = () => {
    if (relatorioGeral) {
      console.log('[RelatorioLider] Exportando dados gerais (sem filtros)');
      exportarRelatorioLider(relatorioGeral);
    } else if (relatorio) {
      console.log('[RelatorioLider] Exportando dados atuais como gerais');
      exportarRelatorioLider(relatorio);
    }
  };

  useEffect(() => {
    carregarRelatorio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (carregando) {
    return <SkeletonLoader />;
  }

  if (erro) {
    return <Alert severity="error" sx={{ mt: 2 }}>{erro}</Alert>;
  }

  if (!relatorio) {
    return <Alert severity="info" sx={{ mt: 2 }}>Nenhum dado disponível</Alert>;
  }

  const { resumo, departamento, porStatus, porPrioridade, porUsuario, tendencias } = relatorio;

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Relatório do Departamento
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {departamento.nome} • {relatorio.periodo.dataInicio} até {relatorio.periodo.dataFim}
        </Typography>
      </Box>

      {/* Filtros Avançados */}
      <FiltrosRelatorioComponent
        onFiltrosChange={handleFiltrosChange}
        tipoRelatorio="lider"
        departamentoId={departamento.id}
        onExportar={handleExportarFiltrado}
        onExportarGeral={handleExportarGeral}
        mostrarBotaoExportar={usuario?.tipo === 'admin' || usuario?.eh_lider}
        filtrosAplicados={filtrosAtivos}
      />

      {/* Cards de Resumo */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 3 }} flexWrap="wrap">
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Total de Tarefas</Typography>
              <Typography variant="h4">{resumo.totalTarefas}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Concluídas</Typography>
              <Typography variant="h4" color="success.main">{resumo.tarefasConcluidas}</Typography>
              <Typography variant="caption" color="text.secondary">
                {formatarPorcentagem(resumo.taxaConclusao)} de conclusão
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Pendentes</Typography>
              <Typography variant="h4" color="warning.main">{resumo.tarefasPendentes}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Atrasadas</Typography>
              <Typography variant="h4" color="error.main">{resumo.tarefasAtrasadas}</Typography>
            </CardContent>
          </Card>
        </Box>
      </Stack>

      {/* Taxa de Conclusão */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Taxa de Conclusão</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={resumo.taxaConclusao}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: obterCorTaxaConclusao(resumo.taxaConclusao)
                  }
                }}
              />
            </Box>
            <Typography variant="h6" sx={{ color: obterCorTaxaConclusao(resumo.taxaConclusao) }}>
              {formatarPorcentagem(resumo.taxaConclusao)}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Tempo médio de conclusão: {formatarTempoConclusao(resumo.tempoMedioConclusao)}
          </Typography>
        </CardContent>
      </Card>

      {/* Tarefas por Status e Prioridade */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Tarefas por Status</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {porStatus.map((item) => (
                      <TableRow key={item.status}>
                        <TableCell>{item.status}</TableCell>
                        <TableCell align="right">
                          <Chip label={item.quantidade} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Tarefas por Prioridade</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Prioridade</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {porPrioridade.map((item) => (
                      <TableRow key={item.prioridade}>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{item.prioridade}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={item.quantidade}
                            size="small"
                            color={
                              item.prioridade === 'urgente' ? 'error' :
                              item.prioridade === 'alta' ? 'warning' :
                              item.prioridade === 'media' ? 'info' : 'default'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Stack>

      {/* Performance por Usuário */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Performance por Usuário</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Usuário</TableCell>
                  <TableCell align="right">Tarefas Ativas</TableCell>
                  <TableCell align="right">Concluídas</TableCell>
                  <TableCell align="right">Atrasadas</TableCell>
                  <TableCell align="right">Taxa de Conclusão</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {porUsuario.map((usuario) => (
                  <TableRow key={usuario.usuarioId}>
                    <TableCell>{usuario.nome}</TableCell>
                    <TableCell align="right">{usuario.tarefasAtivas}</TableCell>
                    <TableCell align="right">
                      <Chip label={usuario.tarefasConcluidas} size="small" color="success" />
                    </TableCell>
                    <TableCell align="right">
                      {usuario.tarefasAtrasadas > 0 ? (
                        <Chip label={usuario.tarefasAtrasadas} size="small" color="error" />
                      ) : (
                        <Chip label="0" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={usuario.taxaConclusao}
                          sx={{
                            width: 100,
                            height: 6,
                            borderRadius: 3,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: obterCorTaxaConclusao(usuario.taxaConclusao)
                            }
                          }}
                        />
                        <Typography variant="body2" sx={{ minWidth: 40 }}>
                          {formatarPorcentagem(usuario.taxaConclusao)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Tendências (últimos 7 dias) */}
      {tendencias.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Tendências (Últimos 7 Dias)</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Criadas</TableCell>
                    <TableCell align="right">Concluídas</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tendencias.map((item) => {
                    const saldo = item.concluidas - item.criadas;
                    return (
                      <TableRow key={item.data}>
                        <TableCell>{new Date(item.data).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell align="right">{item.criadas}</TableCell>
                        <TableCell align="right">{item.concluidas}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            {saldo > 0 ? (
                              <>
                                <TrendingUpIcon fontSize="small" color="success" />
                                <Typography variant="body2" color="success.main">+{saldo}</Typography>
                              </>
                            ) : saldo < 0 ? (
                              <>
                                <TrendingDownIcon fontSize="small" color="error" />
                                <Typography variant="body2" color="error.main">{saldo}</Typography>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">0</Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* ============================================ */}
      {/* NOVAS MÉTRICAS - FASE 2 (ESCOPO DO DEPARTAMENTO) */}
      {/* ============================================ */}

      {/* Gargalos Identificados */}
      {relatorio.gargalosIdentificados && relatorio.gargalosIdentificados.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <GargalosIdentificados dados={relatorio.gargalosIdentificados} />
        </Box>
      )}

      {/* Tarefas Mais Antigas */}
      {relatorio.tarefasMaisAntigas && relatorio.tarefasMaisAntigas.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <TarefasMaisAntigas dados={relatorio.tarefasMaisAntigas} />
        </Box>
      )}

      {/* Tempo Médio por Status */}
      {relatorio.tempoMedioPorStatus && relatorio.tempoMedioPorStatus.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <TempoMedioPorStatus dados={relatorio.tempoMedioPorStatus} />
        </Box>
      )}

      {/* Produtividade Detalhada */}
      {relatorio.produtividadeDetalhada && relatorio.produtividadeDetalhada.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <ProdutividadeDetalhada 
            dados={relatorio.produtividadeDetalhada}
            titulo="Produtividade da Equipe (Score Detalhado)"
          />
        </Box>
      )}
    </Box>
  );
};

export default RelatorioLider;
